import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { QrReader } from 'react-qr-reader';
import { queryAccountByCouponCode } from '../../api/modules/invite';
import { showMessage } from '../../utils';
import AccountSelectDialog from './AccountSelectDialog';
import { useNavigate } from 'react-router-dom';
import { isInApp } from '../../utils/environment';

const QRScanner = ({ onClose }) => {
  const [hasPermission, setHasPermission] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [accounts, setAccounts] = useState(null);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [qrcode, setQrcode] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(() => setHasPermission(true))
        .catch(() => setHasPermission(false));
    } else if (navigator.getUserMedia) {
      navigator.getUserMedia({ video: true },
        () => setHasPermission(true),
        () => setHasPermission(false)
      );
    } else {
      setHasPermission(false);
    }

    return () => setScanning(false);
  }, []);

  const handleScan = async (result) => {
    if (result && scanning) {
      setScanning(false);
      const code = result?.text; // 获取扫描到的二维码内容
      setQrcode(code);
      try {
        await queryAccountByCouponCode({code}, (rst) => {
          const { code: responseCode, data, msg } = rst;
          
          if (responseCode === 200) {
            if (data && data.length > 0) {
              setAccounts(data);
              setShowAccountDialog(true);
            } else {
              showMessage('No valid account found for this code', 'error');
              setScanning(true); // 重新开始扫描
            }
          } else {
            showMessage(msg || 'Verification failed', 'error');
            setScanning(true); // 重新开始扫描
          }
        });
      } catch (error) {
        showMessage('Verification failed. Please try again.', 'error');
        setScanning(true); // 重新开始扫描
      }
    }
  };

  const handleAccountSelect = (account) => {
    setShowAccountDialog(false);
    navigate('/campaigns/invitations/verification-result', { 
      state: { 
        account,
        code: qrcode,
        useType: 'QR Code'
      } 
    });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'black',
        zIndex: 1300,
      }}
    >
      {/* Header */}
      {
        !isInApp() && (
          <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            p: 2,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{ color: 'white', mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontSize: '1.25rem',
              fontWeight: 600,
            }}
          >
            Scan QR Code
          </Typography>
        </Box>
        )
      }

      {/* QR Scanner */}
      {hasPermission && (
        <Box sx={{ height: '100%' }}>
          <QrReader
            constraints={{
              facingMode: 'environment'
            }}
            onResult={handleScan}
            style={{ width: '100%', height: '100%' }}
          />
          
          {/* Scanning Frame */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 280,
              height: 280,
              border: '2px solid white',
              borderRadius: 4,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            }}
          />

          {/* Scanning Instructions */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              left: 0,
              right: 0,
              textAlign: 'center',
              color: 'white',
              px: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                mb: 1,
              }}
            >
              Position QR code within frame
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem',
              }}
            >
              Make sure the QR code is clearly visible
            </Typography>
          </Box>
        </Box>
      )}

      {hasPermission === false && (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography>
            Camera access is required to scan QR codes.
            Please enable camera access in your browser settings.
          </Typography>
        </Box>
      )}

      {/* Account Selection Dialog */}
      <AccountSelectDialog
        open={showAccountDialog}
        accounts={accounts}
        onClose={() => {
          setShowAccountDialog(false);
          setScanning(true); // 关闭弹框后重新开始扫描
        }}
        onSelect={handleAccountSelect}
      />
    </Box>
  );
};

export default QRScanner; 